import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import { moduleColor } from '../ui/modulePalette';
import Modal from '../ui/Modal';
import { IconAlertTriangle, IconCheck, IconCopy, IconEdit } from '../ui/icons';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

function useApiBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, '');
  return '/api';
}

function planLabel(plan) {
  const p = String(plan || '').toLowerCase();
  if (p === 'premium') return 'Premium';
  if (p === 'pro') return 'Pro';
  return 'Essentiel';
}

function maskableNumber(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

const RANGE_OPTIONS = [
  { key: '24h', label: 'Dernières 24 heures' },
  { key: '7d', label: '7 derniers jours' },
  { key: '30d', label: '30 derniers jours' },
];

function topPageLabel(p) {
  const title = String(p?.title || '').trim();
  if (title) return title;
  return 'Page sans titre';
}

export default function TrafficPage({ siteId, site, user, panelSettings }) {
  const [state, setState] = useState({ status: 'loading', error: '', data: null });
  const [editPlan, setEditPlan] = useState(false);
  const [planDraft, setPlanDraft] = useState('essential');
  const [configOpen, setConfigOpen] = useState(false);
  const [health, setHealth] = useState({ status: 'idle', error: '', checks: [] });
  const [collectTest, setCollectTest] = useState({ status: 'idle', error: '' });
  const [pageDetect, setPageDetect] = useState({ status: 'idle', error: '', data: null });

  const defaultRangeFromSettings = useMemo(() => {
    const raw = String(panelSettings?.traffic?.default_range || '').trim().toLowerCase();
    if (raw === '24h' || raw === '7d' || raw === '30d') return raw;
    return '7d';
  }, [panelSettings?.traffic?.default_range]);
  const [selectedRange, setSelectedRange] = useState(defaultRangeFromSettings);
  const [selectedTarget, setSelectedTarget] = useState('site');
  const [selectedPageHash, setSelectedPageHash] = useState('');
  const [detectModal, setDetectModal] = useState(null);

  const [templatePrompt, setTemplatePrompt] = useState(null);
  const [templateDraft, setTemplateDraft] = useState({ project_template_name: '', article_template_name: '' });

  const apiBaseUrl = useApiBaseUrl();
  const accent = moduleColor('traffic');

  const canConfigure = Boolean(String(user?.role || '') === 'admin' && user?.is_superadmin);

  useEffect(() => {
    setSelectedRange(defaultRangeFromSettings);
  }, [siteId, defaultRangeFromSettings]);

  useEffect(() => {
    if (!detectModal || detectModal.status !== 'loading') return;
    const timer = window.setInterval(() => {
      setDetectModal((m) => {
        if (!m || m.status !== 'loading') return m;
        return { ...m, durationMs: Math.max(0, Date.now() - Number(m.startedAt || Date.now())) };
      });
    }, 250);
    return () => window.clearInterval(timer);
  }, [detectModal]);

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...(s || {}), status: 'loading', error: '' }));
    api
      .traffic(Number(siteId), {
        range: selectedRange,
        target: selectedTarget,
        page_hash: selectedTarget === 'site' ? '' : selectedPageHash,
      })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', error: res.error || 'Impossible de charger', data: null });
          return;
        }
        setState({ status: 'ready', error: '', data: res });
        setPlanDraft(String(res.plan || 'essential'));
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', error: 'Impossible de charger', data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, selectedRange, selectedTarget, selectedPageHash]);

  useEffect(() => refresh(), [refresh]);

  const data = state.data || null;
  const trackingId = String(data?.tracking_id || '').trim();
  const plan = String(data?.plan || 'essential');
  const isEssential = plan === 'essential';
  const topPages = useMemo(() => (Array.isArray(data?.top_pages) ? data.top_pages : []), [data]);

  useEffect(() => {
    if (selectedTarget === 'site') return;
    if (!selectedPageHash) return;
    const exists = topPages.some((p) => String(p?.page_hash || '') === String(selectedPageHash));
    if (!exists) {
      setSelectedTarget('site');
      setSelectedPageHash('');
    }
  }, [selectedTarget, selectedPageHash, topPages]);

  useEffect(() => {
    const tpl = data?.template_config || {};
    setTemplateDraft({
      project_template_name: String(tpl.project_template_name || ''),
      article_template_name: String(tpl.article_template_name || ''),
    });
  }, [data?.template_config]);

  const collectUrl = useMemo(() => {
    const base = String(apiBaseUrl || '/api').replace(/\/+$/, '');
    return `${base}/px.php`;
  }, [apiBaseUrl]);

  const embedSnippet = useMemo(() => {
    if (!trackingId) return '';
    return `<script>
  (function(){
    var tid = ${JSON.stringify(trackingId)};
    var urls = [${JSON.stringify(collectUrl)}, ${JSON.stringify(String(collectUrl).replace(/\/px\.php$/, '/t.php'))}];
    function sendVia(url){
      var payload = { tracking_id: tid, events: [{ type: 'pageview', ts_ms: Date.now(), path: location.pathname, title: document.title, ua: navigator.userAgent }] };
      if (navigator.sendBeacon) {
        var ok = navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        if (ok) return Promise.resolve(true);
      }
      return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).then(function(){ return true; });
    }
    function send(){
      try{
        sendVia(urls[0]).catch(function(){ return sendVia(urls[1]); }).catch(function(){});
      } catch(e){}
    }
    if (document.readyState === 'complete') send();
    else window.addEventListener('load', send);
  })();
</script>`;
  }, [trackingId, collectUrl]);

  const chartData = useMemo(() => {
    const labels = Array.isArray(data?.chart?.labels) ? data.chart.labels : [];
    const values = Array.isArray(data?.chart?.values) ? data.chart.values : [];
    return {
      labels,
      datasets: [
        {
          label: 'Visites',
          data: values,
          borderColor: accent,
          backgroundColor: `${accent}22`,
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [data?.chart, accent]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false },
      },
      interaction: { mode: 'nearest', intersect: false },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0 },
          grid: { color: 'rgba(255,255,255,0.08)' },
        },
        x: {
          grid: { color: 'rgba(255,255,255,0.05)' },
        },
      },
    }),
    []
  );

  const currentRangeMetric = useMemo(() => {
    if (selectedRange === '24h') return { label: 'Dernières 24 heures', value: data?.summary?.views_24h };
    if (selectedRange === '7d') return { label: '7 derniers jours', value: data?.summary?.views_7d };
    return { label: '30 derniers jours', value: data?.summary?.views_30d };
  }, [selectedRange, data?.summary?.views_24h, data?.summary?.views_7d, data?.summary?.views_30d]);

  async function copySnippet() {
    if (!embedSnippet) return;
    try {
      await navigator.clipboard.writeText(embedSnippet);
    } catch {
      // ignore
    }
  }

  async function savePlan() {
    if (!canConfigure) return;
    if (!data?.client_id) return;
    const res = await api.trafficPlanUpdate({
      site_id: Number(siteId),
      client_id: Number(data.client_id),
      traffic_plan: String(planDraft || 'essential'),
    });
    if (!res.ok) {
      setState((s) => ({ ...(s || {}), status: 'ready', error: res.error || 'Enregistrement impossible' }));
      return;
    }
    setEditPlan(false);
    refresh();
  }

  useEffect(() => {
    if (!configOpen) return;
    if (!siteId) return;
    let cancelled = false;
    setHealth({ status: 'loading', error: '', checks: [] });
    api
      .trafficHealth(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setHealth({ status: 'error', error: res.error || 'Vérification impossible', checks: res.checks || [] });
          return;
        }
        setHealth({ status: 'ready', error: '', checks: res.checks || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setHealth({ status: 'error', error: 'Vérification impossible', checks: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [configOpen, siteId]);

  useEffect(() => {
    if (!configOpen) return;
    if (!trackingId) return;
    let cancelled = false;
    setCollectTest({ status: 'loading', error: '' });
    api
      .trafficCollectDryRun({ tracking_id: trackingId })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setCollectTest({ status: 'error', error: res.error || 'Test API collecte impossible' });
          return;
        }
        setCollectTest({ status: 'ready', error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setCollectTest({ status: 'error', error: 'Test API collecte impossible' });
      });
    return () => {
      cancelled = true;
    };
  }, [configOpen, trackingId]);

  async function runPageDetection(overrides = {}) {
    if (!siteId) return;
    const startedAt = Date.now();
    setDetectModal({
      status: 'loading',
      startedAt,
      scanned: 0,
      matched: 0,
      data: null,
      error: '',
      durationMs: 0,
    });
    setPageDetect({ status: 'loading', error: '', data: null });
    const res = await api.trafficDetectPages({
      site_id: Number(siteId),
      max_pages: 80,
      project_template_name: overrides.project_template_name ?? templateDraft.project_template_name,
      article_template_name: overrides.article_template_name ?? templateDraft.article_template_name,
    });
    if (!res.ok) {
      const msg = Number(res.status) === 405 ? 'Détection indisponible sur cette version API (déploie api/traffic.php).' : res.error || 'Détection impossible';
      setPageDetect({ status: 'error', error: msg, data: null });
      setDetectModal((m) => ({
        ...(m || {}),
        status: 'error',
        error: msg,
        durationMs: Math.max(0, Date.now() - startedAt),
      }));
      return;
    }
    setPageDetect({ status: 'ready', error: '', data: res.detected || null });
    setDetectModal({
      status: 'ready',
      startedAt,
      scanned: Number(res.detected?.scanned_count || 0),
      matched: Number(res.detected?.matched_count || 0),
      data: res.detected || null,
      error: '',
      durationMs: Math.max(0, Date.now() - startedAt),
    });
    refresh();
  }

  function requestPageDetection() {
    const req = data?.detection_requirements || {};
    if (req.needs_template_prompt) {
      setTemplatePrompt({
        project_template_name: String(templateDraft.project_template_name || ''),
        article_template_name: String(templateDraft.article_template_name || ''),
        req,
      });
      return;
    }
    runPageDetection();
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Trafic Web' }]} />
          <div className="Badges">
            {canConfigure ? (
              <button type="button" className="Button Button--ghost" onClick={() => setConfigOpen(true)} disabled={state.status === 'loading'}>
                <span className="ButtonInline">
                  <IconEdit /> Configuration
                </span>
              </button>
            ) : null}
            <span
              className="Badge"
              style={{
                borderColor: accent,
                color: accent,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 110,
                minHeight: 36,
              }}
            >
              {planLabel(plan)}
            </span>
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card" style={{ marginBottom: 14, padding: 18 }}>
        <div className="TitleMd" style={{ marginBottom: 14 }}>
          Vue d'ensemble du trafic
        </div>

        <div className="Badges" style={{ justifyContent: 'flex-start', marginBottom: 14 }}>
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`Button ${selectedRange === opt.key ? '' : 'Button--ghost'}`}
              onClick={() => setSelectedRange(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {state.status === 'loading' ? (
          <div className="Muted">Chargement…</div>
        ) : state.status === 'error' ? null : (
          <>
            <div className="Notice" style={{ minHeight: 118, padding: 18, marginTop: 2 }}>
              <div className="Muted" style={{ fontSize: 13 }}>
                {currentRangeMetric.label}
              </div>
              <div style={{ fontSize: 28, fontWeight: 820, marginTop: 6 }}>{isEssential ? '—' : maskableNumber(currentRangeMetric.value)}</div>
              <div className="Muted" style={{ marginTop: 2 }}>
                visites sur le site
              </div>
            </div>

            {!isEssential ? (
              <div className="RoleBox" style={{ marginTop: 14 }}>
                <div className="Row" style={{ marginBottom: 10 }}>
                  <div className="LabelStrong">Graphique des visites</div>
                  <div className="Badges">
                    <select
                      className="Select"
                      value={selectedTarget === 'site' ? 'site' : selectedPageHash}
                      onChange={(e) => {
                        const v = String(e.target.value || 'site');
                        if (v === 'site') {
                          setSelectedTarget('site');
                          setSelectedPageHash('');
                        } else {
                          setSelectedTarget('page');
                          setSelectedPageHash(v);
                        }
                      }}
                    >
                      <option value="site">Ensemble du site</option>
                      {topPages.map((p) => (
                        <option key={String(p.page_hash || p.path)} value={String(p.page_hash || '')}>
                          {topPageLabel(p)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ height: 260 }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            ) : null}

            <div className="RoleBox" style={{ marginTop: 14 }}>
              <div className="LabelStrong">Pages les plus visitées ({RANGE_OPTIONS.find((x) => x.key === selectedRange)?.label || 'période'})</div>
              {topPages.length === 0 ? (
                <div className="Muted" style={{ marginTop: 6 }}>
                  Aucune donnée pour le moment.
                </div>
              ) : (
                <div className="List" role="list" style={{ marginTop: 10 }}>
                  {topPages.map((p) => (
                    <div key={`${p.rank}_${p.path}_${p.page_hash || ''}`} className="ListItem">
                      <div style={{ minWidth: 0 }}>
                        <div className="ButtonInline" style={{ justifyContent: 'space-between' }}>
                          <span className="OneLine" style={{ fontWeight: 700 }}>
                            <span className="Muted">#{p.rank}</span> {topPageLabel(p)}
                          </span>
                          {!isEssential && p.views != null ? <span className="Muted">{maskableNumber(p.views)}</span> : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {state.status === 'error' ? (
        <div className="Hint" style={{ marginTop: 10 }}>
          <span className="ButtonInline">
            <IconAlertTriangle /> Vérifie que la migration Trafic Web est appliquée (migrations <code>026_traffic_web.sql</code> et <code>033_traffic_page_labels_and_templates.sql</code>).
          </span>
        </div>
      ) : null}

      {canConfigure && configOpen ? (
        <Modal
          title="Trafic Web — Configuration"
          icon={<IconEdit />}
          onClose={() => setConfigOpen(false)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setConfigOpen(false)}>
              Fermer
            </button>
          }
        >
          <div className="RoleBox">
            <div className="LabelStrong">Tracking</div>
            <div className="Muted" style={{ marginTop: 6 }}>
              <code>{trackingId || '—'}</code>
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
              <button type="button" className="Button Button--ghost" onClick={copySnippet} disabled={!trackingId}>
                <span className="ButtonInline">
                  <IconCopy /> Copier le snippet
                </span>
              </button>
            </div>
          </div>

          <div className="RoleBox" style={{ marginTop: 12 }}>
            <div className="LabelStrong">Intégration</div>
            <div className="Muted" style={{ marginTop: 6 }}>
              Endpoint de collecte : <code>{collectUrl}</code>
            </div>
            {!embedSnippet ? <div className="Muted">Tracking ID manquant.</div> : <textarea className="Textarea" rows={12} value={embedSnippet} readOnly />}
          </div>

          <div className="RoleBox" style={{ marginTop: 12 }}>
            <div className="Row" style={{ marginBottom: 0 }}>
              <div className="LabelStrong">Détection des pages trackées</div>
              <div className="Badges">
                <button type="button" className="Button Button--ghost" onClick={requestPageDetection} disabled={pageDetect.status === 'loading' || !trackingId}>
                  <span className="ButtonInline">
                    {pageDetect.status === 'loading' ? <span className="Spinner" aria-hidden="true" /> : <IconEdit />} Lancer la détection
                  </span>
                </button>
              </div>
            </div>
            <div className="Muted" style={{ marginTop: 8 }}>
              Détecte les pages qui contiennent le script de tracking et récupère leur balise <code>{'<title>'}</code>.
            </div>
            {pageDetect.error ? (
              <div className="Error" role="alert" style={{ marginTop: 10 }}>
                {pageDetect.error}
              </div>
            ) : null}
            {pageDetect.status === 'ready' ? (
              <>
                <div className="Hint" style={{ marginTop: 10 }}>
                  Base: <code>{String(pageDetect.data?.base_url || '—')}</code> · Pages scannées: <strong>{String(pageDetect.data?.scanned_count ?? 0)}</strong> · Pages détectées: <strong>{String(pageDetect.data?.matched_count ?? 0)}</strong>
                </div>
                {Array.isArray(pageDetect.data?.pages) && pageDetect.data.pages.length ? (
                  <div className="List" role="list" style={{ marginTop: 10 }}>
                    {pageDetect.data.pages.map((p) => (
                      <div key={String(p.url || '')} className="ListItem">
                        <div style={{ minWidth: 0 }}>
                          <div className="ButtonInline" style={{ justifyContent: 'space-between' }}>
                            <span className="OneLine" style={{ fontWeight: 700 }}>
                              {String(p.title || 'Sans titre')}
                            </span>
                          </div>
                          <div className="Muted OneLine" style={{ marginTop: 6 }}>
                            {String(p.url || '')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="Muted" style={{ marginTop: 10 }}>
                    Aucune page trackée détectée.
                  </div>
                )}
              </>
            ) : null}
          </div>

          <div className="RoleBox" style={{ marginTop: 12 }}>
            <div className="LabelStrong">Vérifications</div>
            <div className="List" role="list" style={{ marginTop: 10 }}>
              <div className="ListItem">
                <span className="ButtonInline">
                  {health.status === 'loading' ? <span className="Spinner" aria-hidden="true" /> : health.status === 'error' ? <IconAlertTriangle /> : <IconCheck />}{' '}
                  BDD / schéma
                </span>
              </div>
              <div className="ListItem">
                <span className="ButtonInline">
                  {collectTest.status === 'loading' ? <span className="Spinner" aria-hidden="true" /> : collectTest.status === 'error' ? <IconAlertTriangle /> : <IconCheck />}{' '}
                  API collecte
                </span>
              </div>
            </div>
            {health.error || collectTest.error ? (
              <div className="Error" role="alert" style={{ marginTop: 10 }}>
                {health.error || collectTest.error}
              </div>
            ) : null}
          </div>

          <div className="RoleBox" style={{ marginTop: 12 }}>
            <div className="Row" style={{ marginBottom: 0 }}>
              <div className="LabelStrong">Abonnement Trafic Web</div>
              <div className="Badges">
                <button type="button" className="Button Button--ghost" onClick={() => setEditPlan((v) => !v)}>
                  <span className="ButtonInline">
                    <IconEdit /> {editPlan ? 'Fermer' : 'Modifier'}
                  </span>
                </button>
              </div>
            </div>
            {editPlan ? (
              <div className="FormRow3" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Plan</label>
                  <select className="Select" value={planDraft} onChange={(e) => setPlanDraft(e.target.value)}>
                    <option value="essential">Essentiel</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div className="Field" />
                <div className="Field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="button" className="Button" onClick={savePlan} disabled={!data?.client_id}>
                    <span className="ButtonInline">
                      <IconCheck /> Enregistrer
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="Muted" style={{ marginTop: 8 }}>
                {data?.client_id ? (
                  <>
                    Ce plan est appliqué au contact principal du site (client_id: <code>{String(data.client_id)}</code>).
                  </>
                ) : (
                  <>Aucun contact n’est lié à ce site.</>
                )}
              </div>
            )}
          </div>
        </Modal>
      ) : null}

      {templatePrompt ? (
        <Modal
          title="Template de contenus"
          icon={<IconEdit />}
          onClose={() => setTemplatePrompt(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setTemplatePrompt(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={() => {
                  const payload = {
                    project_template_name: String(templatePrompt.project_template_name || ''),
                    article_template_name: String(templatePrompt.article_template_name || ''),
                  };
                  setTemplateDraft(payload);
                  setTemplatePrompt(null);
                  runPageDetection(payload);
                }}
              >
                Continuer
              </button>
            </>
          }
        >
          <div className="Muted" style={{ marginBottom: 10 }}>
            Pour améliorer le tri des pages « classiques » et des pages « projet/article », indique le nom des templates utilisés sur le site.
          </div>
          {templatePrompt.req?.projects_enabled ? (
            <div className="Field" style={{ marginBottom: 10 }}>
              <label className="Label">Template Projets</label>
              <input
                className="Input"
                value={templatePrompt.project_template_name}
                onChange={(e) => setTemplatePrompt((s) => ({ ...(s || {}), project_template_name: e.target.value }))}
                placeholder="ex: projet.php ou templates/project"
              />
            </div>
          ) : null}
          {templatePrompt.req?.articles_enabled ? (
            <div className="Field">
              <label className="Label">Template Articles</label>
              <input
                className="Input"
                value={templatePrompt.article_template_name}
                onChange={(e) => setTemplatePrompt((s) => ({ ...(s || {}), article_template_name: e.target.value }))}
                placeholder="ex: article.php ou templates/article"
              />
            </div>
          ) : null}
        </Modal>
      ) : null}

      {detectModal ? (
        <Modal
          title="Détection du tracking"
          icon={detectModal.status === 'error' ? <IconAlertTriangle /> : detectModal.status === 'ready' ? <IconCheck /> : <IconEdit />}
          onClose={() => setDetectModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setDetectModal(null)}>
              {detectModal.status === 'loading' ? 'Masquer' : 'Fermer'}
            </button>
          }
        >
          {detectModal.status === 'loading' ? (
            <div className="Notice">
              <span className="ButtonInline">
                <span className="Spinner" aria-hidden="true" /> Analyse en cours…
              </span>
              <div className="Muted" style={{ marginTop: 8 }}>
                Pages scannées: <strong>{Number(detectModal.scanned || 0)}</strong> · Détections: <strong>{Number(detectModal.matched || 0)}</strong>
              </div>
              <div className="Muted" style={{ marginTop: 4 }}>
                Progression: en cours ({Math.max(0, Number(detectModal.durationMs || 0))} ms)
              </div>
            </div>
          ) : null}
          {detectModal.status === 'error' ? (
            <div className="Error" role="alert">
              {detectModal.error || 'Détection impossible'}
            </div>
          ) : null}
          {detectModal.status === 'ready' ? (
            <div className="RoleBox">
              <div className="LabelStrong">Bilan</div>
              <div className="Muted" style={{ marginTop: 8 }}>
                Pages scannées: <strong>{Number(detectModal.scanned || 0)}</strong>
              </div>
              <div className="Muted">
                Pages détectées: <strong>{Number(detectModal.matched || 0)}</strong>
              </div>
              <div className="Muted">
                Durée: <strong>{Math.max(0, Number(detectModal.durationMs || 0))} ms</strong>
              </div>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
